import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import {
  addAgentExample,
  removeAgentExample,
  testAgent,
  clearAgentTest,
} from "../../Redux/Actions/agent";
import { selectAgentTestResult } from "../../Redux/Selectors";
import { AgentForm } from "./form";

const option = (value: string, label: string, help: string) => (
  <FormControlLabel
    value={value}
    control={<Radio color="primary" />}
    label={
      <span>
        {label}
        <span className="agentRadioHelp">{help}</span>
      </span>
    }
  />
);

export const SendingCard: React.FC<{
  form: AgentForm;
  setField: (field: keyof AgentForm, value: any) => void;
}> = ({ form, setField }) => (
  <div className="aiCard">
    <div className="aiCardTitle">Sending</div>
    <div className="aiCardHint">Choose how much the agent may do on its own.</div>
    <FormControlLabel
      control={
        <Switch
          color="primary"
          checked={form.enabled}
          onChange={(event) => setField("enabled", event.target.checked)}
        />
      }
      label="Agent on: draft a reply for every new email reply"
    />
    <div className="inboxSectionTitle" style={{ marginTop: 12 }}>
      Replies
    </div>
    <RadioGroup
      className="agentRadio"
      value={form.sendMode}
      onChange={(event) => setField("sendMode", event.target.value)}
    >
      {option("review", "Review every reply", "Nothing is sent until you approve it.")}
      {option(
        "auto",
        "Auto-send safe replies",
        "Clear, low-risk replies are sent automatically. Unsafe or uncertain replies always wait for your review."
      )}
    </RadioGroup>
    <div className="inboxSectionTitle">Video</div>
    <RadioGroup
      className="agentRadio"
      value={form.videoMode}
      onChange={(event) => setField("videoMode", event.target.value)}
    >
      {option("off", "Off", "Text replies only.")}
      {option("manual", "Manual", "Click Generate video on a draft when you want one.")}
      {option(
        "auto",
        "Automatic video for every reply",
        "Each reply draft gets a video made with your default avatar."
      )}
    </RadioGroup>
    {form.videoMode !== "off" && (
      <div className="aiMuted">
        Videos use your avatars. <Link to="/avatars">Manage avatars</Link>
      </div>
    )}
  </div>
);

export const ExamplesCard: React.FC<{ examples: any[] }> = ({ examples }) => {
  const dispatch = useDispatch();
  const saving: boolean = useSelector((state: any) => state.agent.savingExamples);
  const [situation, setSituation] = useState("");
  const [response, setResponse] = useState("");

  const add = () => {
    if (!situation.trim() || !response.trim()) return;
    dispatch(addAgentExample({ situation: situation.trim(), preferredResponse: response.trim() }));
    setSituation("");
    setResponse("");
  };

  return (
    <div className="aiCard">
      <div className="aiCardTitle">Teach by example</div>
      <div className="aiCardHint">
        When the agent gets something wrong, add the real situation and the reply you would have
        sent. Newest examples shape the voice first. Examples are saved immediately.
      </div>
      <div className="agentExampleRow">
        <TextField
          label="Prospect situation or message"
          value={situation}
          onChange={(event) => setSituation(event.target.value)}
          variant="outlined"
          size="small"
          multiline
        />
        <TextField
          label="Preferred response"
          value={response}
          onChange={(event) => setResponse(event.target.value)}
          variant="outlined"
          size="small"
          multiline
        />
        <Button
          variant="outlined"
          onClick={add}
          disabled={saving || !situation.trim() || !response.trim()}
        >
          Add
        </Button>
      </div>
      {examples.length === 0 && <div className="aiMuted">No examples yet.</div>}
      {examples.map((example: any, index: number) => (
        <div className="agentExampleItem" key={`${index}-${example.situation}`}>
          <div>
            <div className="aiMuted">{example.situation}</div>
            <div className="agentExampleResponseText">{example.preferredResponse}</div>
          </div>
          <Button
            size="small"
            className="aiDangerText"
            aria-label="Remove example"
            disabled={saving}
            onClick={() =>
              dispatch(removeAgentExample(examples.filter((_: any, i: number) => i !== index)))
            }
          >
            <DeleteIcon fontSize="small" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export const TestCard: React.FC = () => {
  const dispatch = useDispatch();
  const testing: boolean = useSelector((state: any) => state.agent.testing);
  const result: any = useSelector(selectAgentTestResult);
  const [message, setMessage] = useState("");

  return (
    <div className="aiCard">
      <div className="aiCardTitle">Test the agent</div>
      <div className="aiCardHint">
        Runs a full reply cycle against a sample message using the saved settings. Nothing is sent
        and nothing is saved.
      </div>
      <TextField
        label="Paste a sample prospect message"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        multiline
        rows={3}
        className="aiField"
      />
      <div className="aiActions">
        <Button
          variant="contained"
          className="aiPrimaryButton"
          startIcon={<PlayArrowIcon />}
          onClick={() => dispatch(testAgent({ subject: "Test message", body: message }))}
          disabled={testing || !message.trim()}
        >
          {testing ? "Testing..." : "Run test"}
        </Button>
        {result && (
          <Button size="small" onClick={() => dispatch(clearAgentTest())}>
            Clear result
          </Button>
        )}
      </div>
      {result && (
        <div className="agentTestResult">
          {result.error ? (
            <div className="aiErrorText">{result.error}</div>
          ) : (
            <>
              <div className="aiChips" style={{ marginBottom: 10 }}>
                <span className={`aiChip ${result.decision === "reply" ? "aiChip--info" : "aiChip--muted"}`}>
                  {result.decision}
                </span>
                <span className="aiMuted">
                  {result.classification} · confidence {Math.round((result.confidence || 0) * 100)}%
                  {result.critic ? ` · critic ${result.critic.verdict}` : ""}
                </span>
              </div>
              <div className="agentTestBody">{result.body || "(no reply)"}</div>
              {result.reason && (
                <div className="aiMuted" style={{ marginTop: 10 }}>
                  Reason: {result.reason}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
